import { Skill } from '@sp/data/bif';
import { Button, LoaderBlock } from '@sp/ui/elements';
import { cls } from '@sp/util/helpers';
import { ROUTES } from '@sp/util/router';
import { getSkillClassNameAndColor, KnownSkillColor } from '@sp/util/skills';
import { FC } from 'react';
import { PieChart } from 'react-minimal-pie-chart';
import { useNavigate } from 'react-router-dom';

type SkillTagProps = {
  readonly title: string;
  readonly className: string;
  readonly color: string;
  readonly value: number;
};

function mapSkillsToTags(skills: ReadonlyArray<Skill>): ReadonlyArray<SkillTagProps> {
  return skills.map(skill => ({
    title: skill.tag?.name ?? 'Unknown skill',
    value: skill.points,
    ...getSkillClassNameAndColor((skill.tag?.color ?? '') as KnownSkillColor),
  }));
}

export const Skills: FC<{ skills: readonly Skill[]; isLoading: boolean; owner?: boolean }> = ({
  skills,
  isLoading,
  owner = false,
}) => {
  const navigate = useNavigate();

  if (isLoading) return <LoaderBlock />;

  const tags = mapSkillsToTags(skills);

  return (
    <>
      <div className="flex justify-between mb-6">
        <span className="text-xl font-semibold">{owner ? 'Your skillset' : 'Skillset'}</span>
      </div>
      <div className="flex flex-row items-center">
        <div className="flex items-center justify-center relative w-40 h-40 mr-6">
          <PieChart
            className="absolute top-0 left-0"
            viewBoxSize={[160, 160]}
            rounded
            radius={80}
            lineWidth={10}
            startAngle={tags.some(({ value }) => value !== 0) ? 240 : 5}
            center={[80, 80]}
            paddingAngle={12}
            data={tags.map(({ title, color, value }) => ({ title, color, value: value + 1 }))}
          ></PieChart>
          <div className="text-center">
            <div className="text-[2rem] leading-10 font-semibold">{tags.reduce((a, b) => a + b.value, 0)}</div>
            <div className="text-xs font-semibold">skill points</div>
          </div>
        </div>
        <div className="flex flex-col text-xs">
          <h5 className="mb-2 mt-0 text-xs font-semibold">Topics</h5>
          {tags.map((t, i) => (
            <div key={i} className="flex flex-row items-center mb-2">
              <div
                className={cls(
                  'w-5 h-5 flex items-center justify-center rounded-md bg-brand-cyan text-white font-bold',
                  t.className,
                )}
              >
                {t.value}
              </div>
              <div className="ml-1">{t.title}</div>
            </div>
          ))}
        </div>
      </div>
      {owner ? (
        <div className="rounded-2.5xl bg-light p-4 mt-4">
          <div className="font-bold mb-2">Explore new challenges</div>
          <div className="text-xs">
            Reap the benefits of the Self+ collection of curated challenges. Never stop learning — you&apos;ll thank
            yourself later.
          </div>
          <Button
            color="black"
            size="sm"
            onClick={() => navigate(ROUTES.DISCOVER_URL)}
            className="mt-4 px-3 py-[0.4rem] !text-base"
          >
            Master a new skill
          </Button>
        </div>
      ) : null}
    </>
  );
};
